import { useState } from 'react';
import { cn } from '../../../../../../lib/utils';
import { cardClass, primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../../../styling/stylingHelper';
import {
  backfillAICheckerRange,
  processBackfillAICheckerRange,
  backfillAICheckerCandidates,
} from '../../../../../../services/admin/adminService';
import { History, Layers, RotateCw } from 'lucide-react';
import type { TFn } from '../../types';

/**
 * One-off admin tool over a historical date range on one mailbox, in two modes:
 * - 'audit' (backfill): audit-only — never re-runs the platform handler, stored orders/revenue are
 *   untouched. Pick a range that does NOT overlap already-audited days so you don't overwrite traces.
 * - 'process' (backprocess): full notify treatment — reprocesses each email (recovering missed
 *   orders/revenue/facturen; idempotent via the processing dedup) THEN audits it. Emails that already
 *   have an audit row are skipped entirely. Price-alert emails to clients are suppressed for the run.
 * Both run in the background; rows appear in the feed as they complete, progress in server logs.
 */
export function AICheckerBackfillPanel({ t, mode = 'audit', open: controlledOpen, onClose }: {
  t: TFn; mode?: 'audit' | 'process' | 'candidates';
  /** When provided the panel is controlled (no trigger button): the "More" overflow menu owns opening. */
  open?: boolean; onClose?: () => void;
}) {
  const controlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ? controlledOpen : internalOpen;
  const close = () => { if (controlled) onClose?.(); else setInternalOpen(false); };
  // Candidates backfill is safe across every mailbox at once, so default it to 'all'.
  const [account, setAccount] = useState(mode === 'candidates' ? 'all' : 'cloud64');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const keys = mode === 'process'
    ? {
        run: 'adminTesting.review.backprocessRun',
        title: 'adminTesting.review.backprocessTitle',
        desc: 'adminTesting.review.backprocessDesc',
        confirm: 'adminTesting.review.backprocessConfirm',
        started: 'adminTesting.review.backprocessStarted',
      }
    : mode === 'candidates'
    ? {
        run: 'adminTesting.review.candidatesRun',
        title: 'adminTesting.review.candidatesTitle',
        desc: 'adminTesting.review.candidatesDesc',
        confirm: 'adminTesting.review.candidatesConfirm',
        started: 'adminTesting.review.candidatesStarted',
      }
    : {
        run: 'adminTesting.review.backfillRun',
        title: 'adminTesting.review.backfillTitle',
        desc: 'adminTesting.review.backfillDesc',
        confirm: 'adminTesting.review.backfillConfirm',
        started: 'adminTesting.review.backfillStarted',
      };

  const run = async () => {
    if (!startDate || !endDate) return;
    const ok = window.confirm(t(keys.confirm, { account, startDate, endDate }));
    if (!ok) return;
    try {
      setStatus('loading');
      setMessage(null);
      const { count, skipped } =
        mode === 'process'
          ? await processBackfillAICheckerRange(account, startDate, endDate).then((r) => ({ count: r.toProcess, skipped: r.skipped }))
          : mode === 'candidates'
          ? await backfillAICheckerCandidates(account, startDate, endDate).then((r) => ({ count: r.toBackfill, skipped: 0 }))
          : await backfillAICheckerRange(account, startDate, endDate).then((r) => ({ count: r.toBackfill, skipped: r.skipped }));
      setStatus('done');
      setMessage(t(keys.started, { count, skipped }));
    } catch (e: any) {
      setStatus('error');
      setMessage(e.message || 'Failed to start backfill');
    }
  };

  const inputCls = cn('px-3 py-2 rounded-lg border', 'border-gray-300 dark:border-gray-600', 'bg-white dark:bg-gray-700', primaryText, smallTextSize);

  return (
    <>
      {!controlled && (
        <button
          onClick={() => setInternalOpen(true)}
          className={cn('inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 whitespace-nowrap', primaryText, smallTextSize, semiBoldText)}
        >
          {mode === 'process' ? <RotateCw className="w-4 h-4 shrink-0" /> : mode === 'candidates' ? <Layers className="w-4 h-4 shrink-0" /> : <History className="w-4 h-4 shrink-0" />}
          {t(keys.run)}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 dark:bg-black/50 p-4" onClick={close}>
          <div className={cn(cardClass, 'w-full max-w-lg p-6 space-y-4')} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h3 className={cn(primaryText, semiBoldText)}>{t(keys.title)}</h3>
              <button onClick={close} className={cn(secondaryText, 'hover:opacity-70')} aria-label="Close">✕</button>
            </div>
            <p className={cn(secondaryText, 'text-xs')}>{t(keys.desc)}</p>
            <div className="flex flex-wrap items-center gap-3">
              <select value={account} onChange={(e) => setAccount(e.target.value)} className={inputCls}>
                {mode === 'candidates' && <option value="all">All mailboxes</option>}
                <option value="cloud64">culinarycloud64@gmail.com</option>
                <option value="cloud63">culinarycloud63@gmail.com</option>
              </select>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
              <span className={cn(secondaryText, smallTextSize)}>→</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} />
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={run}
                disabled={status === 'loading' || !startDate || !endDate}
                className={cn('px-4 py-2 rounded-lg bg-purple-600 dark:bg-purple-600 text-white dark:text-white hover:bg-purple-700 dark:hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed', smallTextSize, semiBoldText)}
              >
                {status === 'loading' ? t('adminTesting.review.backfillRunning') : t(keys.run)}
              </button>
            </div>
            {message && (
              <div className={cn('text-xs', status === 'error' ? 'text-red-600 dark:text-red-400' : secondaryText)}>{message}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
